/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import { ProjectElementMeta }          from '@/models/project/project.element.meta';
import { AbstractProjectElement }      from './project/abstract.project.element';
import { ProjectElementType }          from './project/project.element.type';
import { Blob, BlobType }              from '@/models/blob';
import { UserCommandMergeResult }      from '@/models/user.command.merge.result';
import { DiamondCrossingsMergeResult } from '@/models/diamond.crossings.merge.result';

/**
 *
 * @class ProjectFactory
 *
 * @brief allows for construction of typed project elements without direct knowledge of the type. This
 * avoids nasty circular dependencies.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      April 2020
 *
 */
export class ProjectElementFactory {

  /** create a typed (empty/new) project */
  public static fromObject(type: ProjectElementType, options: any): AbstractProjectElement {

    const defaults = {
      project:  this,
      id:       '',
      name:     '',
      value:    0,
      kind:     BlobType.None,
      parent:   null,
      publish:  false,
      type:     BlobType.None,
      key:      '',
      icon:     '',
      job:      null,
      input:    '',
      input2:   '',
      output:   '',
      kpi:      '',
      kpix:     '',
      created:  new Date(),
      instance: 0,
      data:     [],
      tablekey: '',
      label:    null,
      handle:   null,
      fav:      null,
      start:    '',
      end:      '',
      prefix:   '',
      batch:    '',
      error:    '',
      task:     ''
    };

    const args = Object.assign({}, defaults, options);

    if (typeof(args.type) == 'string') {

      args.type = Blob.typeofString(args.type)
    }

    switch (type) {

      case ProjectElementType.Simulation: {

        return new ProjectElementMeta.classNameToCtor['SimulationElement'](
          args.project,
          args.id,
          args.parent,
          args.name,
          args.publish
        );
      }

      case ProjectElementType.SimulationGroup: {

        return new ProjectElementMeta.classNameToCtor['SimulationGroupElement'](
          args.project,
          args.id,
          args.name,
          args.parent,
          args.publish
        );
      }

      case ProjectElementType.LocomotiveModel: {

        return new ProjectElementMeta.classNameToCtor['LocomotiveModelElement'](
          args.project,
          args.id,
          args.parent,
          args.name,
          args.publish
        );
      }

      case ProjectElementType.BatchGroup: {

        return new ProjectElementMeta.classNameToCtor['BatchGroupElement'](
          args.project,
          args.id,
          args.parent,
          args.name,
          args.key,
          args.prefix,
          args.publish
        );
      }

      case ProjectElementType.CorridorBatch: {

        return new ProjectElementMeta.classNameToCtor['CorridorBatchElement'](
          args.project,
          args.id,
          args.parent,
          args.name,
          args.key,
          args.prefix,
          args.batch,
          args.error,
          args.publish
        );
      }

      case ProjectElementType.OperationalMeasures: {

        return new ProjectElementMeta.classNameToCtor['OperationalMeasuresElement'](
          args.project,
          args.id,
          args.parent,
          args.name,
          args.publish
        );
      }

      case ProjectElementType.BatchSummaries: {

        return new ProjectElementMeta.classNameToCtor['BatchSummariesElement'](
          args.project,
          args.id,
          args.parent,
          args.name,
          args.publish
        );
      }

      case ProjectElementType.LocoMotiveModelResult: {

        return new ProjectElementMeta.classNameToCtor['LocomotiveModelResultElement'](
          args.project,
          args.id,
          args.handle,
          args.fav,
          args.name,
          args.start,
          args.end,
          args.parent,
          args.publish
        );
      }

      case ProjectElementType.Manifest: {

        return new ProjectElementMeta.classNameToCtor['ManifestElement'](
          args.project,
          args.id,
          args.parent
        );
      }

      case ProjectElementType.Inputs: {

        return new ProjectElementMeta.classNameToCtor['InputsElement'](
          args.project,
          args.id,
          args.parent,
          args.publish
        );
      }

      case ProjectElementType.Property: {

        return new ProjectElementMeta.classNameToCtor['PropertyElement'](
          args.project,
          args.id,
          args.name,
          args.value,
          args.data,
          args.parent,
          args.publish,
          ProjectElementType.Property,
          args.icon
        );
      }

      case ProjectElementType.KPI: {

        return new ProjectElementMeta.classNameToCtor['KPIElement'](
          args.project,
          args.id,
          args.input,
          args.output,
          args.kpi,
          args.parent,
          args.publish
        );
      }

      case ProjectElementType.KPIX: {

        return new ProjectElementMeta.classNameToCtor['KPIXElement'](
          args.project,
          args.id,
          args.input,
          args.output,
          args.kpix,
          args.parent,
          args.publish
        );
      }

      case ProjectElementType.KPITable: {

        return new ProjectElementMeta.classNameToCtor['KPITableElement'](
          args.project,
          args.id,
          args.instance,
          args.data,
          args.created,
          args.tablekey,
          args.parent,
          args.publish
        );
      }

      case ProjectElementType.LineSimResult: {

        return new ProjectElementMeta.classNameToCtor['LineSimJobElement'](
          args.project,
          args.id,
          args.job,
          args.parent,
          args.publish
        );
      }

      case ProjectElementType.BatchSimResult: {

        return new ProjectElementMeta.classNameToCtor['BatchSimJobElement'](
          args.project,
          args.id,
          args.job,
          args.parent,
          args.task,
          args.publish
        );
      }

      case ProjectElementType.LineSimResultCalculated: {

        return new ProjectElementMeta.classNameToCtor['LineSimElement'](
          args.project,
          args.id,
          args.input,
          args.output,
          args.start,
          args.end,
          args.name,
          args.parent,
          args.publish
        );
      }

      case ProjectElementType.RegressionResult: {

        return new ProjectElementMeta.classNameToCtor['RegressionElement'](
          args.project,
          args.id,
          args.input,
          args.start,
          args.end,
          args.name,
          args.parent,
          args.publish
        );
      }

      case ProjectElementType.BatchMeasure: {

        return new ProjectElementMeta.classNameToCtor['BatchMeasureElement'](
          args.project,
          args.id,
          args.input,
          args.input2,
          args.name,
          args.parent,
          args.publish
        );
      }

      case ProjectElementType.CorridorGroup: {

        return new ProjectElementMeta.classNameToCtor['CorridorGroupElement'](
          args.project,
          args.id,
          args.name,
          args.parent,
          args.publish
        );
      }

      case ProjectElementType.Results: {

        return new ProjectElementMeta.classNameToCtor['ResultsElement'](
          args.project,
          args.id,
          args.parent,
          args.publish
        );
      }

      case ProjectElementType.Snapshots: {

        return new ProjectElementMeta.classNameToCtor['SnapshotsElement'](
          args.project,
          args.id,
          args.parent,
          args.publish
        );
      }

      case ProjectElementType.Dimension: {

        return new ProjectElementMeta.classNameToCtor['DimensionElement'](
          args.project,
          args.id,
          args.name,
          args.kind,
          args.parent,
          args.publish
        );
      }

      case ProjectElementType.Blob : {

        return new ProjectElementMeta.classNameToCtor['BlobElement'](
          args.project,
          args.id,
          args.type,
          args.key,
          args.parent,
          args.publish,
          args.label
        );
      }

      case ProjectElementType.UserCommands : {

        return new ProjectElementMeta.classNameToCtor['UserCommandsElement'](
          args.project,
          args.id,
          args.type,
          args.key,
          args.parent,
          args.publish,
          args.label
        );

      }

      case ProjectElementType.UserCommandsResult : {

        return new ProjectElementMeta.classNameToCtor['UserCommandsResultElement'](
          args.project,
          args.id,
          args.type,
          args.key,
          args.parent,
          args.publish,
          args.label,
          args.data as UserCommandMergeResult
        );

      }


      case ProjectElementType.DiamondCrossings : {

        return new ProjectElementMeta.classNameToCtor['DiamondCrossingsElement'](
          args.project,
          args.id,
          args.type,
          args.key,
          args.parent,
          args.publish,
          args.label
        );

      }

      case ProjectElementType.DiamondCrossingsResult : {

        return new ProjectElementMeta.classNameToCtor['DiamondCrossingsResultElement'](
          args.project,
          args.id,
          args.type,
          args.key,
          args.parent,
          args.publish,
          args.label,
          args.data as DiamondCrossingsMergeResult
        );

      }

      case ProjectElementType.Root : {

        return new ProjectElementMeta.classNameToCtor['RootElement'](
          args.project,
          args.id,
          args.name,
          args.icon
        );
      }
    }

    /* no match  */

    return  null;
  }
}
